import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { bindKey, get } from '@sama/micro-dash';
import { ShapeState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { VectorTaggingToolObjectless } from '../../../../cross-team-shared/tools-names';
import { ShapeGroupOrderState } from '../../../../team-apis/content/content-connector';
import { EventTrackingValue } from '../../../../team-apis/core/event-tracking';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import {
  AnnotationOutput,
  AnnotationOutput2D,
} from '../../../../team-apis/otterdam/outputs/annotation-output';
import { ClickxOptions } from '../../../../team-apis/raccoons/clickx-options';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import {
  MultiClickShapeTool,
  MultiClickShapeToolHelper,
} from '../shared/tool-helpers/multi-click-shape-tool-helper';
import { PolygonInferenceService } from './polygon-inference.service';
import { PolygonTool } from './polygon-tool';

class ClickxToMultiClickToolAdapter implements MultiClickShapeTool {
  name: VectorTaggingToolObjectless;
  notifyDrawFinished = bindKey(this.tool, 'notifyDrawFinished');

  constructor(
    private clickxOptions: ClickxOptions,
    private tool: PolygonTool,
  ) {
    this.name = tool.name;
  }

  buildShape(initialValues: ShapeState2D): ShapeState2D {
    return {
      ...initialValues,
      type: 'model_input_points',
      source: {
        name: 'polygon_tool_clickx',
        model_id: this.clickxOptions.model_id,
      },
    };
  }

  handleInProgressMouseDown(
    helper: MultiClickShapeToolHelper,
    event: MouseEvent,
  ) {
    const modelInputPoints = this.addModelInputPoint(helper);

    if (modelInputPoints.length === 4) {
      void helper.stopListening(event);
    } else {
      helper.trackAddInProgressSubpointEvent(modelInputPoints.length - 1, {
        eventName: 'add_model_input_point',
      });
    }
  }

  prepareShapeForCommit(shape: ShapeState2D) {
    shape.type = 'polygon';
  }

  private getModelInputPointsStore(
    helper: MultiClickShapeToolHelper,
  ): Store<Point2D[] | undefined> {
    return helper.getKeyLocationStore()('model_input_points');
  }

  private addModelInputPoint(helper: MultiClickShapeToolHelper) {
    const modelInputPointsStore = this.getModelInputPointsStore(helper);
    const modelInputPoints = [
      ...(modelInputPointsStore.state() || []),
      this.tool.getBoundedImagePoint(),
    ];

    modelInputPointsStore.set(modelInputPoints);
    return modelInputPoints;
  }
}

export class ClickxToolHelper extends MultiClickShapeToolHelper {
  private polygonInferenceService: PolygonInferenceService;

  constructor(
    tool: PolygonTool,
    overlayStore: Store<ShapeGroupOrderState>,
    injector: Injector,
  ) {
    const store = injector.get(AbstractSensorContentStore2D);
    const clickxOptions = getClickxOptionsOrThrow(store.state().output);
    const adapter = new ClickxToMultiClickToolAdapter(clickxOptions, tool);
    super(adapter, overlayStore, injector);
    this.polygonInferenceService = injector.get(PolygonInferenceService);
  }

  override cancel(eventTrackingValue: EventTrackingValue) {
    if (this.hasFinishedListening) {
      // fix me if you change this code
      // eslint-disable-next-line no-param-reassign -- fix me!
      eventTrackingValue = {
        ...eventTrackingValue,
        model_request_in_flight: true,
      };
    }
    super.cancel(eventTrackingValue);
  }

  protected override async performAsyncStep() {
    this.shellConnector.trackEvent({
      id: 'send_model_request',
      bucket: 'annotation',
      ...this.getEventContext(),
    });

    const releaseInteractionNone =
      this.shellConnector.restrictInteractions('none');
    void this.completion.finally(releaseInteractionNone);

    // Issue? This could be used here, but then a different output claims it
    // before we actually end up using it.
    await this.polygonInferenceService.infer(
      this.getKeyLocationStore(),
      this,
      this.shapeIndex,
    );
  }
}

export function getClickxOptionsOrThrow(output: AnnotationOutput) {
  const clickxOptions = getClickxOptions(output);
  if (!clickxOptions) {
    throw new Error('clickx is not available on this output');
  }
  return clickxOptions;
}

export function getClickxOptions(output: AnnotationOutput) {
  const clickxOptions: ClickxOptions | undefined = get(output, [
    'meta',
    'clickx_options',
  ]);
  return clickxOptions?.enabled ? clickxOptions : undefined;
}

export function isClickxActive(output: AnnotationOutput2D) {
  const availableTools = get(output, ['meta', 'available_tools']);
  return availableTools.includes('Polygon') && getClickxOptions(output);
}
